import { useEffect, useState } from "react";
import Seo from "@/lib/seo/Seo";
import { getSeoForPath } from "@/lib/seo/routes.mjs";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowRight,
  ChevronRight,
  Check,
  Send,
  Search,
  MessageSquare,
  ListChecks,
  Sparkles,
  Upload,
  FileText,
  X,
} from "lucide-react";
import { API_BASE } from "@/lib/site";
import { getTrackingPayload } from "@/lib/utm";
import { initTracking, trackConversion, pushLeadDataLayer } from "@/lib/tracking";
import Reveal from "@/components/Reveal";
import ParticleField from "@/components/ParticleField";
import { ProcessIllustration } from "@/components/illustrations/FintechIllustrations";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const BUSINESS_TYPES = [
  "Restaurant",
  "Deli / Convenience",
  "Grocery",
  "Smoke Shop",
  "Retail",
  "Salon / Spa",
  "Service Business",
  "E-Commerce",
  "Other",
];

const VOLUME = [
  "Under $10,000",
  "$10,000 – $25,000",
  "$25,000 – $50,000",
  "$50,000 – $100,000",
  "$100,000 – $250,000",
  "$250,000+",
];

const INTERESTS = [
  "Payment processing",
  "POS system",
  "Free POS program",
  "Cash discount / dual pricing",
  "Traditional pricing",
  "E-commerce payments",
  "Mobile payments",
];

const TIMEFRAMES = ["ASAP", "This week", "This month", "Just exploring"];

const NEXT_STEPS = [
  {
    icon: Search,
    title: "We take a look at your request",
    description:
      "Our team reviews your business type, payment needs, and current setup.",
  },
  {
    icon: MessageSquare,
    title: "We reach out with the right questions",
    description:
      "We'll contact you to better understand what you're looking for and what would make the most sense for your business.",
  },
  {
    icon: ListChecks,
    title: "You get clear options",
    description:
      "We explain your available processing, POS, cash discount, or equipment options in a simple way.",
  },
  {
    icon: Sparkles,
    title: "You choose what works best",
    description:
      "No pressure, no obligation — move forward only if the solution feels right for your business.",
  },
];

export default function GetStarted() {
  const [form, setForm] = useState({
    business_name: "",
    contact_name: "",
    phone: "",
    email: "",
    business_type: "",
    business_location: "",
    monthly_volume: "",
    current_processor: "",
    current_pos: "",
    interests: [],
    timeframe: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadInfo, setUploadInfo] = useState(null);

  // Load tracking pixels once on mount; conversion fires only on success.
  useEffect(() => { initTracking(); }, []);

  const onPickFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) { toast.error("File too large (max 10 MB)."); return; }
    setFile(f);
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const res = await axios.post(`${API_BASE}/uploads/statement`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setUploadInfo({ storage_path: res.data.storage_path, original_filename: res.data.original_filename });
      toast.success("Statement uploaded.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed.");
      setFile(null);
    } finally {
      setUploading(false);
    }
  };
  const clearFile = () => { setFile(null); setUploadInfo(null); };

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setSelect = (k) => (v) => setForm({ ...form, [k]: v });
  const toggleInterest = (label) => {
    setForm((f) => {
      const has = f.interests.includes(label);
      return {
        ...f,
        interests: has
          ? f.interests.filter((x) => x !== label)
          : [...f.interests, label],
      };
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.business_name || !form.contact_name || !form.phone || !form.email) {
      toast.error("Please complete the required fields.");
      return;
    }
    setSubmitting(true);
    try {
      const r = await axios.post(`${API_BASE}/get-started`, {
        ...form,
        statement_storage_path: uploadInfo?.storage_path || null,
        statement_filename: uploadInfo?.original_filename || null,
        tracking: getTrackingPayload(),
      });
      trackConversion("GetStartedLead");
      pushLeadDataLayer("lead_get_started_submit", "get_started", r?.data?.id);
      setSuccess(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Could not submit. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="get-started-page" data-prerender-ready="true">
      <Seo {...getSeoForPath("/get-started")} />
      {/* HERO — matches the rest of the site */}
      <section className="relative bg-[var(--swipe-navy)] text-white overflow-hidden border-b border-white/10">
        <div className="absolute inset-0 grid-faint opacity-30 pointer-events-none" />
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse 55% 65% at 78% 50%, rgba(0,114,255,0.30) 0%, transparent 65%)" }} />
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse 40% 50% at 20% 30%, rgba(0,114,255,0.14) 0%, transparent 60%)" }} />
        <div aria-hidden="true" className="absolute -top-24 -left-20 w-[420px] h-[420px] rounded-full pointer-events-none" style={{ background: "rgba(0,114,255,0.18)", filter: "blur(110px)" }} />
        <div aria-hidden="true" className="absolute -bottom-32 right-[12%] w-[480px] h-[480px] rounded-full pointer-events-none" style={{ background: "rgba(0,114,255,0.16)", filter: "blur(120px)" }} />
        <div aria-hidden="true" className="absolute top-[30%] right-[2%] w-2 h-2 rounded-full bg-[var(--swipe-accent)] opacity-70 pointer-events-none" />
        <div aria-hidden="true" className="absolute top-[18%] left-[40%] w-1.5 h-1.5 rounded-full bg-[#7DB6FF] opacity-60 pointer-events-none" />
        <div aria-hidden="true" className="absolute bottom-[20%] left-[8%] w-1.5 h-1.5 rounded-full bg-[#7DB6FF] opacity-50 pointer-events-none" />

        <ParticleField density={36} connectDistance={130} className="absolute inset-0 w-full h-full" />

        <div className="container-swipe relative pt-14 pb-16 lg:pt-20 lg:pb-24">
          <nav className="flex items-center gap-2 text-[13px] text-white/50 mb-8">
            <Link to="/" className="hover:text-white">Home</Link>
            <ChevronRight className="w-3 h-3" />
            <span className="text-white/80">Get Started</span>
          </nav>

          <div className="grid lg:grid-cols-[1.05fr,1fr] gap-10 lg:gap-12 items-center">
            <Reveal>
              <div className="relative">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/8 border border-white/15 text-[10.5px] uppercase tracking-[0.22em] font-bold text-[#7DB6FF] mb-6">
                  <span className="w-1.5 h-1.5 rounded-full bg-[var(--swipe-accent)] animate-pulse" />
                  New Application
                </div>
                <h1 className="font-display-tight text-[2.5rem] sm:text-[3.25rem] lg:text-[4.25rem] xl:text-[4.75rem] font-extrabold leading-[1.04] text-white uppercase tracking-[-0.03em]">
                  Get <span className="text-[var(--swipe-accent)]">Started</span>
                </h1>
                <p className="mt-7 text-[17px] md:text-[18px] text-white/75 leading-[1.7] max-w-xl">
                  Tell us about your business and our team will help you choose the right
                  payment processing or POS solution.
                </p>
              </div>
            </Reveal>

            <Reveal variant="slide-right" delay={120}>
              <div className="relative">
                <ProcessIllustration transparent className="relative w-full h-auto max-w-[560px] mx-auto" />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* FORM + WHAT HAPPENS NEXT */}
      <section className="section container-swipe">
        {success ? (
          <Reveal>
            <div className="max-w-2xl mx-auto bg-white border border-line rounded-2xl p-8" data-testid="get-started-success">
              <div className="w-12 h-12 rounded-full bg-[var(--swipe-accent-soft)] text-[var(--swipe-accent)] flex items-center justify-center">
                <Check className="w-6 h-6" strokeWidth={3} />
              </div>
              <h2 className="mt-5 font-display-tight text-2xl font-black text-[var(--swipe-navy)]">
                Thank you for contacting Swipetronics.
              </h2>
              <p className="mt-3 text-slate-600 leading-relaxed">
                We received your information and will review it shortly. A specialist will contact you to discuss your processing options, POS needs, and possible savings.
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link to="/" className="btn-secondary-swipe">Back to home</Link>
                <Link to="/resources" className="btn-outline-swipe">Browse resources</Link>
              </div>
            </div>
          </Reveal>
        ) : (
          <div className="grid lg:grid-cols-[1.45fr,1fr] gap-10">
            {/* FORM */}
            <Reveal>
              <form
                onSubmit={submit}
                className="bg-white border border-line rounded-2xl p-8 lg:p-10"
                data-testid="get-started-form"
              >
                <div className="overline mb-3">Your information</div>
                <h3 className="font-display-tight text-2xl font-black text-[var(--swipe-navy)]">
                  Tell us about your business.
                </h3>

                <div className="mt-8 grid sm:grid-cols-2 gap-5">
                  <div>
                    <Label htmlFor="g-business">Business name *</Label>
                    <Input id="g-business" value={form.business_name} onChange={update("business_name")} required className="mt-2" data-testid="get-started-business-name" />
                  </div>
                  <div>
                    <Label htmlFor="g-contact">Contact name *</Label>
                    <Input id="g-contact" value={form.contact_name} onChange={update("contact_name")} required className="mt-2" data-testid="get-started-contact-name" />
                  </div>
                  <div>
                    <Label htmlFor="g-phone">Phone number *</Label>
                    <Input id="g-phone" value={form.phone} onChange={update("phone")} required className="mt-2" data-testid="get-started-phone" />
                  </div>
                  <div>
                    <Label htmlFor="g-email">Email *</Label>
                    <Input id="g-email" type="email" value={form.email} onChange={update("email")} required className="mt-2" data-testid="get-started-email" />
                  </div>
                  <div>
                    <Label htmlFor="g-btype">Business type</Label>
                    <Select value={form.business_type} onValueChange={setSelect("business_type")}>
                      <SelectTrigger className="mt-2" id="g-btype" data-testid="get-started-business-type">
                        <SelectValue placeholder="Select business type" />
                      </SelectTrigger>
                      <SelectContent>
                        {BUSINESS_TYPES.map((b) => (
                          <SelectItem key={b} value={b}>{b}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="g-loc">Business address or city/state</Label>
                    <Input id="g-loc" value={form.business_location} onChange={update("business_location")} placeholder="e.g., Brooklyn, NY" className="mt-2" data-testid="get-started-location" />
                  </div>
                  <div>
                    <Label htmlFor="g-vol">Approximate monthly card volume</Label>
                    <Select value={form.monthly_volume} onValueChange={setSelect("monthly_volume")}>
                      <SelectTrigger className="mt-2" id="g-vol" data-testid="get-started-volume">
                        <SelectValue placeholder="Select range" />
                      </SelectTrigger>
                      <SelectContent>
                        {VOLUME.map((v) => (
                          <SelectItem key={v} value={v}>{v}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="g-time">How soon are you looking to get started?</Label>
                    <Select value={form.timeframe} onValueChange={setSelect("timeframe")}>
                      <SelectTrigger className="mt-2" id="g-time" data-testid="get-started-timeframe">
                        <SelectValue placeholder="Select timeframe" />
                      </SelectTrigger>
                      <SelectContent>
                        {TIMEFRAMES.map((t) => (
                          <SelectItem key={t} value={t}>{t}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="g-cp">Current processor</Label>
                    <Input id="g-cp" value={form.current_processor} onChange={update("current_processor")} placeholder="e.g., Square, Heartland" className="mt-2" data-testid="get-started-processor" />
                  </div>
                  <div>
                    <Label htmlFor="g-pos">Current POS system</Label>
                    <Input id="g-pos" value={form.current_pos} onChange={update("current_pos")} placeholder="e.g., Clover, Toast" className="mt-2" data-testid="get-started-pos" />
                  </div>
                </div>

                {/* Interests — multi-select pills */}
                <div className="mt-7">
                  <Label>What are you interested in?</Label>
                  <div className="mt-3 flex flex-wrap gap-2.5" data-testid="get-started-interests">
                    {INTERESTS.map((opt) => {
                      const active = form.interests.includes(opt);
                      return (
                        <button
                          type="button"
                          key={opt}
                          onClick={() => toggleInterest(opt)}
                          data-testid={`get-started-interest-${opt.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                          className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-full border text-[13px] font-bold transition-all duration-200 ${
                            active
                              ? "bg-[var(--swipe-accent)] text-white border-[var(--swipe-accent)] shadow-[0_10px_22px_-10px_rgba(0,114,255,0.55)]"
                              : "bg-white text-[var(--swipe-navy)] border-line hover:border-[var(--swipe-accent)] hover:text-[var(--swipe-accent)]"
                          }`}
                        >
                          {active && <Check className="w-3.5 h-3.5" strokeWidth={3.2} />}
                          {opt}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="mt-7">
                  <Label htmlFor="g-msg">Anything else we should know?</Label>
                  <Textarea id="g-msg" value={form.message} onChange={update("message")} rows={4} className="mt-2" data-testid="get-started-message" />
                </div>

                <div className="mt-7">
                  <Label>Optional: Upload latest merchant statement (PDF, JPG, PNG)</Label>
                  <div className="mt-2">
                    {!file ? (
                      <label htmlFor="g-file" className="flex items-center justify-center gap-3 px-5 py-6 border-2 border-dashed border-line rounded-xl cursor-pointer hover:border-[var(--swipe-accent)] hover:bg-[var(--swipe-muted)] transition-colors" data-testid="get-started-file-label">
                        <Upload className="w-5 h-5 text-[var(--swipe-accent)]" />
                        <span className="text-sm text-slate-600">Click to upload (max 10 MB)</span>
                        <input id="g-file" type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png,.webp" onChange={onPickFile} data-testid="get-started-file-input" />
                      </label>
                    ) : (
                      <div className="flex items-center gap-3 px-4 py-3 border border-line rounded-xl bg-white">
                        <FileText className="w-5 h-5 text-[var(--swipe-accent)] shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold text-[var(--swipe-navy)] truncate">{file.name}</div>
                          <div className="text-[11px] text-slate-500">{uploading ? "Uploading…" : (uploadInfo ? "Uploaded ✓" : "")}</div>
                        </div>
                        <button type="button" onClick={clearFile} className="p-1 rounded hover:bg-slate-100"><X className="w-4 h-4" /></button>
                      </div>
                    )}
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={submitting || uploading}
                  className="btn-primary-swipe mt-8 w-full justify-center disabled:opacity-60 disabled:cursor-not-allowed"
                  data-testid="get-started-submit"
                >
                  {submitting ? "Submitting…" : <>Submit My Information <Send className="w-4 h-4" /></>}
                </button>
                <p className="mt-4 text-[11.5px] text-slate-500 leading-[1.6]" data-testid="get-started-consent">
                  By submitting this form, you agree to be contacted by Swipetronics regarding your request. Message and data rates may apply. Reply STOP to opt out of text messages.
                </p>
              </form>
            </Reveal>

            {/* WHAT HAPPENS NEXT */}
            <Reveal variant="slide-right" delay={120}>
              <aside className="space-y-5 lg:sticky lg:top-28" data-testid="what-happens-next">
                <div className="bg-[var(--swipe-navy)] text-white rounded-2xl p-7 relative overflow-hidden">
                  <div aria-hidden="true" className="absolute -top-12 -right-12 w-48 h-48 rounded-full bg-[var(--swipe-accent)]/22 blur-3xl" />
                  <div className="relative">
                    <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-white/10 border border-white/15 text-[10px] uppercase tracking-[0.22em] font-extrabold text-[#7DB6FF]">
                      <span className="w-1.5 h-1.5 rounded-full bg-[var(--swipe-accent)]" />
                      Onboarding
                    </div>
                    <h4 className="mt-5 font-display-tight text-2xl font-extrabold leading-tight tracking-[-0.01em]" style={{ color: "#FFFFFF" }}>
                      What Happens Next
                    </h4>
                    <p className="mt-3 text-sm leading-relaxed" style={{ color: "rgba(255,255,255,0.72)" }}>
                      Four simple steps from request to a solution that fits your business.
                    </p>
                  </div>
                </div>

                <ol className="space-y-4">
                  {NEXT_STEPS.map((step, i) => {
                    const Icon = step.icon;
                    return (
                      <li
                        key={step.title}
                        className="group relative bg-white border border-line rounded-2xl p-5 transition-all duration-300 hover:-translate-y-0.5 hover:border-[var(--swipe-accent)]/40 hover:shadow-[0_18px_36px_-22px_rgba(0,114,255,0.35)]"
                        data-testid={`next-step-${i + 1}`}
                      >
                        <div className="flex items-start gap-4">
                          <div className="relative shrink-0">
                            <div className="w-11 h-11 rounded-xl bg-[var(--swipe-accent-soft)] text-[var(--swipe-accent)] flex items-center justify-center transition-all duration-300 group-hover:bg-[var(--swipe-accent)] group-hover:text-white group-hover:scale-110">
                              <Icon className="w-5 h-5" strokeWidth={2.2} />
                            </div>
                            <span
                              aria-hidden="true"
                              className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-[var(--swipe-navy)] text-white text-[10px] font-extrabold flex items-center justify-center shadow-[0_4px_10px_-2px_rgba(6,26,58,0.5)]"
                            >
                              {i + 1}
                            </span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <h5 className="font-display-tight text-[15.5px] font-extrabold text-[var(--swipe-navy)] leading-tight tracking-[-0.01em]">
                              {step.title}
                            </h5>
                            <p className="mt-1.5 text-[13.5px] text-slate-600 leading-[1.65]">
                              {step.description}
                            </p>
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ol>

                <div className="bg-[var(--swipe-accent-soft)] border border-[var(--swipe-accent)]/15 rounded-2xl p-6">
                  <p className="text-[13px] text-[var(--swipe-navy)] leading-relaxed">
                    <span className="font-extrabold">Prefer a phone call?</span> Reach our
                    team directly from the {" "}
                    <Link to="/contact" className="text-[var(--swipe-accent)] hover:underline font-bold inline-flex items-center gap-1">
                      Contact page <ArrowRight className="w-3.5 h-3.5" />
                    </Link>
                  </p>
                </div>
              </aside>
            </Reveal>
          </div>
        )}
      </section>
    </div>
  );
}
